import React, {useEffect, useMemo} from 'react';
import {Polyline, useMap} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-polylineoffset';
import 'leaflet-polylinedecorator';

/**
 * Flèches de sens sur une ligne (leaflet-polylinedecorator). Le décorateur ignore `offset` : il ne porte que le milieu.
 * Il se redessine seul sur `moveend` ; ici : ajout / retrait et mise à jour quand positions, couleur ou opacité changent.
 */
export function FlechesSens({positions, couleur, opacite}) {
    const map = useMap();
    useEffect(() => {
        if (!positions || positions.length < 2) return undefined;
        const decorateur = L.polylineDecorator(positions, {
            patterns: [{
                offset: 24,
                repeat: 180,
                symbol: L.Symbol.arrowHead({pixelSize: 9, polygon: false, pathOptions: {stroke: true, color: couleur, weight: 2, opacity: opacite}}),
            }],
        });
        decorateur.addTo(map);
        return () => { decorateur.remove(); };
    }, [map, positions, couleur, opacite]);
    return null;
}

/**
 * Tracé d'une journée, partagé par l'atelier et la page Scénario : casing blanc 7 px sous la couleur (4 px actif, 3 px inactif),
 * aller décalé de +4 px, retour de −4 px et en pointillé, milieu sans décalage et porteur des flèches.
 * `morceaux` = {aller, milieu, retour} (traceRules.morceauxTrace) ; `facteur` = estompage au survol d'une autre journée ;
 * `avecMilieu` faux : aller et retour seuls (bascule « tracés » de l'atelier).
 */
function TraceJournee({morceaux, couleur, actif = true, facteur = 1, avecMilieu = true, eventHandlers}) {
    const poids = actif ? 4 : 3;
    const opacite = (actif ? 0.95 : 0.6) * facteur;
    const casing = 0.9 * facteur;
    const lignes = useMemo(() => [
        {cle: 'aller', positions: morceaux.aller, offset: 4},
        {cle: 'milieu', positions: avecMilieu ? morceaux.milieu : [], offset: 0},
        {cle: 'retour', positions: morceaux.retour, offset: -4, dashArray: '8 6'},
    ].filter((l) => l.positions.length > 1), [morceaux, avecMilieu]);
    return (
        <>
            {lignes.map((l) => (
                <Polyline key={`casing-${l.cle}`} positions={l.positions} eventHandlers={eventHandlers}
                          pathOptions={{color: '#fff', weight: 7, opacity: casing, offset: l.offset}}/>
            ))}
            {lignes.map((l) => (
                <Polyline key={`ligne-${l.cle}`} positions={l.positions} eventHandlers={eventHandlers}
                          pathOptions={{color: couleur, weight: poids, opacity: opacite, offset: l.offset, ...(l.dashArray ? {dashArray: l.dashArray} : {})}}/>
            ))}
            {avecMilieu && <FlechesSens positions={morceaux.milieu} couleur={couleur} opacite={opacite}/>}
        </>
    );
}

export default React.memo(TraceJournee);
